// src/pages/EventPage.js
import React, { useEffect, useState } from 'react';
import { collection, query, where, orderBy, onSnapshot, doc, getDoc, getDocs, addDoc } from 'firebase/firestore';
import { updateLastModified } from '../utils/backup';
import { db, auth } from '../firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Header from '../components/Header';
import EventEditor from './EventEditor';
import ConfirmationDialog from '../components/ConfirmationDialog';
import '../styles.css';

function EventPage() {
  const [sessions, setSessions] = useState([]);
  const [event, setEvent] = useState(null);
  const [syncStatus, setSyncStatus] = useState('syncing');
  const [vehicleId, setVehicleId] = useState(null);
  const [vehicleName, setVehicleName] = useState('');
  const [showEventEditor, setShowEventEditor] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [eventVehicleIds, setEventVehicleIds] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'time', direction: 'desc' });
  const [showNewSessionDialog, setShowNewSessionDialog] = useState(false);
  const [newSessionName, setNewSessionName] = useState('');
  const [newSessionVehicleIds, setNewSessionVehicleIds] = useState([]);
  const params = useParams();
  const eventId = params.id;
  const [user, loading, error] = useAuthState(auth);
  const navigate = useNavigate();
  const location = useLocation();
  // For shared events, read/write from the owner's account.
  const ownerUid = location.state?.ownerUid || null;
  const dataUid = ownerUid || (user ? user.uid : null);

  useEffect(() => {
    if (loading) {
      setSyncStatus('syncing');
      return;
    }
    if (error || !user || !eventId) {
      console.error('Auth error, no user, or no eventId:', error);
      setSyncStatus('Error: Not logged in or invalid event');
      navigate('/');
      return;
    }

    // Fetch event and vehicle data
    const fetchEvent = async () => {
      setSyncStatus('syncing');
      try {
        let fetchedVehicleId = location.state?.vehicleId;
        // Fetch event
        const eventDoc = await getDoc(doc(db, `users/${dataUid}/events/${eventId}`));
        let eventData = eventDoc.exists() ? { id: eventDoc.id, ...eventDoc.data() } : null;
        if (!eventData) {
          console.error('Event not found:', eventId);
          setSyncStatus('Error: Event not found');
          return;
        }
        setEvent(eventData);
        const evVehicleIds = eventData.vehicleIds || (eventData.vehicleId ? [eventData.vehicleId] : []);
        setEventVehicleIds(evVehicleIds);
        fetchedVehicleId = eventData.vehicleId || evVehicleIds[0];
        // Fetch vehicle name
        if (fetchedVehicleId) {
          const vehicleDoc = await getDoc(doc(db, `users/${dataUid}/vehicles/${fetchedVehicleId}`));
          if (vehicleDoc.exists()) {
            setVehicleName(vehicleDoc.data().name || 'Unnamed Vehicle');
          } else {
            console.warn('Vehicle not found:', fetchedVehicleId);
          }
          setVehicleId(fetchedVehicleId);
        } else {
          console.error('Invalid vehicleId:', fetchedVehicleId);
          setSyncStatus('Error: Invalid vehicle');
        }
        // Fetch vehicles for EventEditor
        const vehiclesQuery = query(collection(db, `users/${dataUid}/vehicles`));
        const vehiclesSnap = await getDocs(vehiclesQuery);
        const vehiclesData = vehiclesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setVehicles(vehiclesData);
        setSyncStatus(navigator.onLine ? 'synced' : 'offline');
      } catch (err) {
        console.error('Fetch event error:', err.message);
        setSyncStatus(`Error: ${err.message}`);
      }
    };

    // Real-time listener for sessions
    const fetchSessions = () => {
      const sessionsQuery = query(
        collection(db, `users/${dataUid}/sessions`),
        where('eventId', '==', eventId),
        orderBy('time', 'desc')
      );
      const unsubscribe = onSnapshot(sessionsQuery, (snapshot) => {
        const eventSessions = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setSessions(eventSessions);
        setSyncStatus(navigator.onLine ? 'Synced' : 'Offline (cached)');
      }, (err) => {
        console.error('Fetch sessions error:', err.message);
        setSyncStatus(`Error: ${err.message}`);
      });
      return unsubscribe;
    };

    fetchEvent();
    const unsubscribeSessions = fetchSessions();

    // Update sync status on connectivity
    const updateSyncStatus = () => {
      setSyncStatus(navigator.onLine ? 'synced' : 'offline');
    };
    window.addEventListener('online', updateSyncStatus);
    window.addEventListener('offline', updateSyncStatus);

    return () => {
      unsubscribeSessions();
      window.removeEventListener('online', updateSyncStatus);
      window.removeEventListener('offline', updateSyncStatus);
    };
  }, [user, loading, error, eventId, location.state, navigate]);

  const newSession = () => {
    if (eventVehicleIds.length === 0) {
      alert('Vehicle ID not loaded for this event');
      return;
    }
    // Single-vehicle event: keep the original direct flow.
    if (eventVehicleIds.length === 1) {
      navigate('/session-setup', { state: { vehicleId: eventVehicleIds[0], eventId, ownerUid } });
      return;
    }
    // Multi-vehicle event: ask which cars took part.
    setNewSessionName('');
    setNewSessionVehicleIds([...eventVehicleIds]);
    setShowNewSessionDialog(true);
  };

  const confirmNewSession = async () => {
    if (newSessionVehicleIds.length === 0) return;
    const sessionGroupId = `grp_${Date.now()}`;
    // Default the session time to now (datetime-local format), shared across the group.
    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const time = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
    setShowNewSessionDialog(false);
    try {
      // Eagerly create one (setup-less) session doc per selected vehicle so the
      // session appears in the list with a Setup Sheet button for each car.
      await Promise.all(newSessionVehicleIds.map(vid => addDoc(
        collection(db, `users/${dataUid}/sessions`),
        {
          vehicleId: Number(vid),
          eventId,
          sessionName: newSessionName,
          comment: '',
          time,
          sessionGroupId,
          sessionVehicleIds: newSessionVehicleIds,
          setup: {},
          encrypted: false,
          timestamp: Date.now(),
        }
      )));
      await updateLastModified(user);
    } catch (err) {
      console.error('Create session error:', err.message);
      alert('Failed to create session: ' + err.message);
    }
  };

  // Open the session data view for a group (first vehicle that has a saved doc).
  const openSessionData = (g) => {
    const firstDoc = g.roster.map(vid => g.byVehicle[vid]).find(Boolean) || Object.values(g.byVehicle)[0];
    if (!firstDoc) return;
    navigate(`/session-data/${firstDoc.id}`, { state: { vehicleId: firstDoc.vehicleId, eventId, ownerUid } });
  };

  // Open the setup sheet for a specific vehicle within a group.
  const openSessionSetup = (g, vid, e) => {
    if (e) e.stopPropagation();
    const existing = g.byVehicle[vid];
    if (existing) {
      navigate(`/session-setup/${existing.id}`, { state: { vehicleId: vid, eventId, ownerUid } });
    } else {
      // No doc yet for this car — create one within the same group.
      navigate('/session-setup', {
        state: {
          vehicleId: vid,
          eventId,
          ownerUid,
          sessionGroupId: g.groupId,
          sessionVehicleIds: g.roster,
          sessionName: g.sessionName,
          time: g.time,
        },
      });
    }
  };

  const formatSessionTime = (t) => {
    const d = new Date((t || '').replace(' ', 'T'));
    if (isNaN(d.getTime())) return t;
    return d.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  // Parse a lap time like "1:23.456" or "83.4" into seconds for sorting.
  const parseLapTime = (lap) => {
    if (lap === undefined || lap === null || lap === '' || lap === 'N/A') return null;
    const str = String(lap).trim();
    const parts = str.split(':');
    let seconds;
    if (parts.length === 2) {
      seconds = parseInt(parts[0], 10) * 60 + parseFloat(parts[1]);
    } else {
      seconds = parseFloat(str);
    }
    return isNaN(seconds) ? null : seconds;
  };

  const vehicleNameById = (id) => {
    const v = vehicles.find(veh => String(veh.id) === String(id));
    return v ? (v.name || 'Unnamed Vehicle') : 'Unnamed Vehicle';
  };

  // Group the flat session list into logical sessions by sessionGroupId.
  // Legacy sessions with no sessionGroupId form a group of one (keyed by doc id).
  const sessionGroups = (() => {
    const groups = new Map();
    sessions.forEach(s => {
      const groupId = s.sessionGroupId || s.id;
      if (!groups.has(groupId)) {
        groups.set(groupId, {
          groupId,
          sessionName: s.sessionName || '',
          time: s.time || '',
          comment: s.comment || '',
          roster: [],
          byVehicle: {},
        });
      }
      const g = groups.get(groupId);
      // Prefer the most complete shared metadata available across docs.
      if (!g.sessionName && s.sessionName) g.sessionName = s.sessionName;
      if (!g.time && s.time) g.time = s.time;
      if (!g.comment && s.comment) g.comment = s.comment;
      g.byVehicle[s.vehicleId] = s;
      // Roster = union of each doc's declared roster (falls back to its own vehicleId).
      const declared = s.sessionVehicleIds && s.sessionVehicleIds.length ? s.sessionVehicleIds : [s.vehicleId];
      declared.forEach(vid => { if (!g.roster.includes(vid)) g.roster.push(vid); });
    });
    return Array.from(groups.values());
  })();

  const groupSortValue = (g, key) => {
    if (key === 'time') {
      const d = new Date((g.time || '').replace(' ', 'T'));
      return isNaN(d.getTime()) ? null : d.getTime();
    }
    if (key === 'bestLap') {
      // Fastest best lap across the group's vehicles.
      const vals = Object.values(g.byVehicle).map(s => parseLapTime(s.bestLap)).filter(v => v !== null);
      return vals.length ? Math.min(...vals) : null;
    }
    if (key === 'numLaps') {
      const vals = Object.values(g.byVehicle).map(s => parseInt(s.numLaps, 10)).filter(n => !isNaN(n));
      return vals.length ? Math.max(...vals) : null;
    }
    return null;
  };

  const sortedGroups = [...sessionGroups].sort((a, b) => {
    const va = groupSortValue(a, sortConfig.key);
    const vb = groupSortValue(b, sortConfig.key);
    // Push missing values to the bottom regardless of direction.
    if (va === null && vb === null) return 0;
    if (va === null) return 1;
    if (vb === null) return -1;
    return sortConfig.direction === 'asc' ? va - vb : vb - va;
  });

  const requestSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      // Default direction: best lap ascending (fastest first), others descending.
      return { key, direction: key === 'bestLap' ? 'asc' : 'desc' };
    });
  };

  const sortIndicator = (key) =>
    sortConfig.key === key ? (sortConfig.direction === 'asc' ? ' ▲' : ' ▼') : '';

  const handleClose = () => {
    if (showEventEditor) {
      setShowDiscardConfirm(true);
    } else {
      navigate('/');
    }
  };

  const confirmDiscard = () => {
    setShowDiscardConfirm(false);
    navigate('/');
  };

  const formatDateRange = (start, end) => {
    if (!start) return '';
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const startDate = new Date(start);
    const endDate = end ? new Date(end) : startDate;
    const startMonth = months[startDate.getMonth()];
    const endMonth = months[endDate.getMonth()];
    const startDay = startDate.getDate();
    const endDay = endDate.getDate();
    const year = startDate.getFullYear();
    if (startMonth === endMonth) {
      if (startDay === endDay) {
        return `${startMonth} ${startDay}, ${year}`;
      } else {
        return `${startMonth} ${startDay}-${endDay}, ${year}`;
      }
    } else {
      return `${startMonth} ${startDay}-${endMonth} ${endDay}, ${year}`;
    }
  };

  return (
    <div>
      <Header syncStatus={syncStatus} />
      <div className="event-sessions-container">
        <div className="event-info" style={{
          ...(event?.image && event.image !== '/images/default-event.jpg' ? {
            background: `linear-gradient(to bottom, transparent, rgba(0, 0, 0, 0.5)), url(${event.image})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            color: 'white'
          } : {
            background: '#333',
            color: 'white'
          })
        }}>
          <div className="page-title">
            <h1>{event?.name || ''}</h1>
            <button onClick={handleClose} className="close-button">✕</button>
          </div>
          <div className="event-info-details">
            <h2>{(eventVehicleIds.length ? eventVehicleIds.map(vehicleNameById).join(', ') : vehicleName) || ''}</h2>
            <h2>{event?.circuit || ''}</h2>
            <h2>{event ? formatDateRange(event.startDate, event.endDate) : ''}</h2>
          </div>
        </div>

        <div className="sessions-list">
          {sessionGroups.length === 0 && <p>No sessions found. Create one to get started.</p>}
          {sortedGroups.map(g => {
            const isMulti = g.roster.length > 1;
            return (
              <div key={g.groupId} onClick={() => openSessionData(g)} className="session-card">
                <div className="session-card-header">
                  <h1>{g.sessionName || 'Unnamed Session'}</h1>
                </div>
                {isMulti ? (
                  <div className="session-card-vehicles">
                    {g.roster.map(vid => {
                      const s = g.byVehicle[vid];
                      return (
                        <div key={vid} className="session-card-vehicle-row">
                          <span className="session-card-vehicle-name">{vehicleNameById(vid)}</span>
                          <span className="session-card-vehicle-stats">
                            {(s && s.bestLap) || '-'} · {(s && s.numLaps) || '0'} laps
                          </span>
                          <button className="setup-sheet-btn compact" onClick={(e) => openSessionSetup(g, vid, e)}>
                            <span className="material-icons">assignment</span>
                          </button>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <>
                    <div className="session-card-meta">
                      <span>Best Lap: {g.byVehicle[g.roster[0]]?.bestLap || 'N/A'}</span>
                      <span>Laps: {g.byVehicle[g.roster[0]]?.numLaps || '0'}</span>
                    </div>
                    <div className="session-card-actions">
                      <button className="setup-sheet-btn" onClick={(e) => openSessionSetup(g, g.roster[0], e)}>
                        <span className="material-icons">assignment</span>
                        Setup Sheet
                      </button>
                    </div>
                  </>
                )}
                <div className="session-card-notes">
                  <span>Notes: {g.comment || 'No notes'}</span>
                </div>
              </div>
            );
          })}
        </div>
        <table className="sessions-table">
          <thead>
            <tr>
              <th className="sortable-th" onClick={() => requestSort('time')}>Session{sortIndicator('time')}</th>
              <th className="sortable-th" onClick={() => requestSort('bestLap')}>Best Lap{sortIndicator('bestLap')}</th>
              <th className="sortable-th" onClick={() => requestSort('numLaps')}>Laps{sortIndicator('numLaps')}</th>
              <th>Note</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {sortedGroups.map(g => {
              const isMulti = g.roster.length > 1;
              if (!isMulti) {
                const s = g.byVehicle[g.roster[0]];
                return (
                  <tr key={g.groupId} onClick={() => openSessionData(g)} className="session-row">
                    <td>
                      <div className="session-name-cell">{g.sessionName || 'Unnamed Session'}</div>
                      {g.time && <div className="session-datetime">{formatSessionTime(g.time)}</div>}
                    </td>
                    <td>{s?.bestLap || '-'}</td>
                    <td>{s?.numLaps || '-'}</td>
                    <td className="session-notes-cell">{g.comment || '-'}</td>
                    <td className="session-actions-cell">
                      <button className="setup-sheet-btn" onClick={(e) => openSessionSetup(g, g.roster[0], e)}>
                        <span className="material-icons">assignment</span>
                        Setup Sheet
                      </button>
                    </td>
                  </tr>
                );
              }
              // Multi-vehicle: a session header row followed by one sub-row per car.
              return (
                <React.Fragment key={g.groupId}>
                  <tr onClick={() => openSessionData(g)} className="session-row session-group-row">
                    <td>
                      <div className="session-name-cell">{g.sessionName || 'Unnamed Session'}</div>
                      {g.time && <div className="session-datetime">{formatSessionTime(g.time)}</div>}
                    </td>
                    <td></td>
                    <td></td>
                    <td className="session-notes-cell">{g.comment || '-'}</td>
                    <td></td>
                  </tr>
                  {g.roster.map(vid => {
                    const s = g.byVehicle[vid];
                    return (
                      <tr key={vid} className="session-vehicle-subrow">
                        <td className="session-subrow-vehicle">
                          <span className="material-icons subrow-icon">directions_car</span>
                          {vehicleNameById(vid)}
                        </td>
                        <td>{s?.bestLap || '-'}</td>
                        <td>{s?.numLaps || '-'}</td>
                        <td></td>
                        <td className="session-actions-cell">
                          <button className="setup-sheet-btn multi" onClick={(e) => openSessionSetup(g, vid, e)}>
                            <span className="material-icons">assignment</span>
                            Setup Sheet
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
        <div className="session-buttons">
          <button onClick={newSession} className="btn btn-save">New Session</button>
          <button onClick={() => setShowEventEditor(true)} className="btn btn-secondary">Edit Event</button>
        </div>
      </div>
      {showEventEditor && (
        <EventEditor
          eventId={eventId}
          vehicleId={vehicleId}
          onClose={() => setShowEventEditor(false)}
          vehicles={vehicles}
          ownerUid={ownerUid}
        />
      )}
      {showDiscardConfirm && (
        <ConfirmationDialog
          message="Are you sure you want to discard unsaved changes?"
          onConfirm={confirmDiscard}
          onCancel={() => setShowDiscardConfirm(false)}
        />
      )}
      {showNewSessionDialog && (
        <div className="modal-overlay" onClick={() => setShowNewSessionDialog(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>New Session</h2>
              <button onClick={() => setShowNewSessionDialog(false)} className="close-button">✕</button>
            </div>
            <div className="modal-content">
              <input
                value={newSessionName}
                onChange={(e) => setNewSessionName(e.target.value)}
                placeholder="Session Name"
                className="session-data-input"
                style={{ width: '100%', marginBottom: '16px' }}
              />
              <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '8px' }}>
                Which vehicles took part?
              </div>
              <div className="more-filter-pills">
                {eventVehicleIds.map(vid => {
                  const active = newSessionVehicleIds.includes(vid);
                  return (
                    <button
                      key={vid}
                      className={`filter-pill ${active ? 'filter-pill-active' : ''}`}
                      onClick={() => setNewSessionVehicleIds(prev =>
                        prev.includes(vid) ? prev.filter(v => v !== vid) : [...prev, vid]
                      )}
                    >
                      {vehicleNameById(vid)}
                    </button>
                  );
                })}
              </div>
              <div className="modal-actions">
                <button onClick={() => setShowNewSessionDialog(false)} className="btn btn-secondary">Cancel</button>
                <button
                  onClick={confirmNewSession}
                  className="btn btn-save"
                  disabled={newSessionVehicleIds.length === 0}
                >
                  Create
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EventPage;